import {of, switchMap} from 'rxjs';
import {PayMethodModel, ProfileTypeModel} from 'src/app/shared/model/pay';
import {AdxPaymentModel, AdxPaymentResponse} from '../models/adx-payment';
import {GridModel} from 'src/app/shared/model/page-grid';
import {inject} from '@angular/core';
import {MaskedCardPipe} from 'src/infrastructure/pipes/masked-card.pipe';
import {DatePipe} from '@angular/common';
import {PayService} from 'src/app/shared/service/pay/pay.service';
import {AdxPaymentGridService} from '../service/adx-payment-grid.service';

export const generatePaymentGrid = () => {

    const maskedCardPipe = inject(MaskedCardPipe);
    const datePipe = inject(DatePipe);
    const payService = inject(PayService);
    const paymentGrid: GridModel<AdxPaymentResponse> = {
        gridDataTestId: 'payments-list',
        gridTitle: 'Payments',
        eventButton: { text: 'Create Payment', icon: 'attach_money', disabled: true, tooltip: 'Create payment is temporarily disabled.' },
        replayButton: true,
        defaultSort: { active: 'dateUpdated', direction: 'desc' },
        columns: [
          {
            columnProperty: 'fmsAdxPaymentDetailId',
            columnHeader: 'Payment Fms Number',
            sort: true,
            clickable: true,
            style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
            linkEventFunction: (item:AdxPaymentModel)=> `AdxPayments/${item?.fmsAdxPaymentDetailId}`,
          },
          {
            columnProperty: 'payeeName',
            columnHeader: 'Payee Name',
            sort: true,
            textLeft: true,
          },
          {
            columnProperty: 'vendorName',
            columnHeader: 'Vendor Name',
            sort: true,
            textLeft: true,
          },
          {
            columnProperty: 'payMethodName',
            columnHeader: 'Pay Method',
            sort: true,
            textLeft: true,
          },
          {
            columnProperty: 'profileTypeName',
            columnHeader: 'Profile Type',
            sort: true,
          },
          {
            columnProperty: 'amount',
            columnHeader: 'Amount',
            sort: true,
          },
          {
            columnProperty: 'ckCcNo',
            columnHeader: 'Payment Reference Number',
            sort: true,
            processData: (item:string)=> maskedCardPipe.transform(item),
          },
          {
            columnProperty: 'branchName',
            columnHeader: 'Branch',
            sort: true,
            textLeft: true,
          },
          {
            columnProperty: 'bankName',
            columnHeader: 'Bank',
            sort: true,
            textLeft: true,
          },
          {
            columnProperty: 'paymentDate',
            columnHeader: 'Payment Date',
            sort: true,
            processData: (item:string)=> datePipe.transform(item,'M/dd/yyyy') ?? '',
          },
          {
            columnProperty: 'tripExternalReference',
            columnHeader: 'Trip Record Locator',
            sort: true,
            disablePreventEventLink: true,
            style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
            linkEventFunction: (item:AdxPaymentModel)=> `Trip/${item?.tripId}`,
          },
          {
            columnProperty: 'adxInvoiceId',
            columnHeader: 'Invoice Number',
            sort: true,
            disablePreventEventLink: true,
            style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
            linkEventFunction: (item:AdxPaymentModel)=> `AdxInvoices/${item?.adxInvoiceId}`,
          },
        ],
    
        filters: [
          {
            filterProperty: 'paymentDateFrom',
            filterDisplayName: 'Payment Date From',
            filterType: 'DATE',
          },
          {
            filterProperty: 'paymentDateTo',
            filterDisplayName: 'Payment Date To',
            filterType: 'DATE',
          },
          {
            filterProperty: 'payMethodId',
            filterDisplayName: 'Pay Method',
            filterType: 'DROPDOWN',
            config: {
              dropdownOptions: payService.getPayMethod({all: true}).pipe(switchMap((data)=> of(data.items))),
              dropdownValueProp: 'id',
              dropdownDisplayWith: (item: PayMethodModel) => item.paymethodName,
            }
          },
          {
            filterProperty: 'branchId',
            filterDisplayName: 'Branch',
            filterType: 'AUTOCOMPLETE',
            config: {
              autocompleteTableName: 'Branch',
              autocompleteColumnName: 'Name'
            }
          },
          {
            filterProperty: 'bankId',
            filterDisplayName: 'Bank',
            filterType: 'AUTOCOMPLETE',
            config: {
              autocompleteTableName: 'Bank',
              autocompleteColumnName: 'Name'
            }
          },
          {
            filterProperty: 'profileTypeCode',
            filterDisplayName: 'Profile Type',
            filterType: 'DROPDOWN',
            config: {
              dropdownOptions: payService.getProfileType({all: true}).pipe(switchMap((data)=> of(data.items))),
              dropdownValueProp: 'profileTypeCode',
              dropdownDisplayWith: (item: ProfileTypeModel) => item.profileTypeName,
            }
          },
          {
            filterProperty: 'invoiceId',
            filterDisplayName: 'Invoice Number',
            filterType: 'AUTOCOMPLETE',
            config: {
              autocompleteTableName: 'AdxInvoice',
              autocompleteColumnName: 'Id',
              autocompleteEmitText: true
            }
          },
          {
            filterProperty: 'clientName',
            filterDisplayName: 'Payee Name',
            filterType: 'AUTOCOMPLETE',
            config: {
              autocompleteTableName: 'AdxPaymentDetail',
              autocompleteColumnName: 'PayeeName',
              autocompleteEmitText: true
            }
          },          
          {
            filterProperty: 'adxInvoiceRecordLocator',
            filterDisplayName: 'Trip Record Locator',
            filterType: 'AUTOCOMPLETE',
            config: {
              autocompleteTableName: 'Trip',
              autocompleteColumnName: 'ExternalReference',
              autocompleteEmitText: true
            }
          },
        ],
        gridGetService: inject(AdxPaymentGridService),
    };

    return paymentGrid;
}

